import Subscription from './subscription';
import SubscriptionGuarantor from './subscriptionGuarantor';
import logger from './logger';
import Consumer from './consumer';


export default class Subscriptions
{
    private readonly consumer: Consumer;
    private guarantor: SubscriptionGuarantor;
    private subscriptions: Subscription[];

    /**
     * Constructor
     */
    constructor(consumer: Consumer)
    {
        this.consumer      = consumer;
        this.guarantor     = new SubscriptionGuarantor(this);
        this.subscriptions = [];
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    create(channelName: string|object, mixin: object): Subscription
    {
        const channel      = channelName;
        const params       = typeof channel === 'object' ? channel : { channel };
        const subscription = new Subscription(this.consumer, params, mixin);
        return this.add(subscription);
    }

    remove(subscription: any): Subscription
    {
        this.forget(subscription);
        if (!this.findAll(subscription.identifier).length)
        {
            this.sendCommand(subscription, 'unsubscribe');
        }
        return subscription;
    }

    reject(identifier: string): Subscription[]
    {
        return this.findAll(identifier).map((subscription) =>
        {
            this.forget(subscription);
            this.notify(subscription, 'rejected');
            return subscription;
        });
    }

    reload(): void[]
    {
        return this.subscriptions.map((subscription) =>
            this.subscribe(subscription));
    }

    notifyAll(callbackName: string, ...args: unknown[]): void[]
    {
        return this.subscriptions.map((subscription) =>
            this.notify(subscription, callbackName, ...args));
    }

    confirmSubscription(identifier: string): void
    {
        logger.log(`Subscription confirmed ${identifier}`);
        this.findAll(identifier).map((subscription) =>
            this.guarantor.forget(subscription));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private add(subscription: Subscription): Subscription
    {
        this.subscriptions.push(subscription);
        this.consumer.ensureActiveConnection();
        this.notify(subscription, 'initialized');
        this.subscribe(subscription);
        return subscription;
    }

    private forget(subscription: Subscription): Subscription
    {
        this.guarantor.forget(subscription);
        this.subscriptions = (this.subscriptions.filter((s) => s !== subscription));
        return subscription;
    }

    private findAll(identifier: string): Subscription[]
    {
        return this.subscriptions.filter((s) => s.identifier === identifier);
    }

    private notify(subscription: any, callbackName: string, ...args: unknown[]): void
    {
        let subscriptions;
        if (typeof subscription === 'string')
        {
            subscriptions = this.findAll(subscription);
        }
        else
        {
            subscriptions = [subscription];
        }

        subscriptions.map((subscription) =>
            (typeof subscription[callbackName] === 'function' ? subscription[callbackName](...args) : undefined));
    }

    private subscribe(subscription: any): void
    {
        if (this.sendCommand(subscription, 'subscribe'))
        {
            this.guarantor.guarantee(subscription);
        }
    }

    private sendCommand(subscription: any, command: string): boolean
    {
        const { identifier } = subscription;
        return this.consumer.send({ command, identifier });
    }
}
